import React, { useState, useEffect } from 'react';
import { Table, Row, Col } from 'react-bootstrap';
import InputField from "../../../SalesForm/InputfieldSales";
import SalesPaymentDetails from "../../../SalesForm/Salespaymentdetails";
import baseURL from "../../../../../../Url/NodeBaseURL";

const EstimateSaleReturnForm = ({ 
  setSaleReturnData, 
  saleReturnData,
  selectedMobile 
}) => {
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [returnData, setReturnData] = useState({
    invoice_number: '',
    // Keep return date internally for saving; UI filter date is separate below
    date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
    return_reason: ''
  });
  // Separate filter date to match v1 behavior (no filtering by default)
  const [filterDate, setFilterDate] = useState('');

  // Initialize invoice selection from existing parent saleReturnData (rehydrate on remount)
  useEffect(() => {
    if (!returnData.invoice_number && saleReturnData && saleReturnData.length > 0) {
      const last = saleReturnData[saleReturnData.length - 1];
      setReturnData(prev => ({
        ...prev,
        invoice_number: last.invoice_number || last.original_invoice_number || prev.invoice_number,
        date: last.return_date || prev.date,
        return_reason: last.return_reason || prev.return_reason,
      }));
    }
  }, [saleReturnData, returnData.invoice_number]);

  // Debug logging for saleReturnData changes
  useEffect(() => {
    console.log('[EstimateSaleReturnForm] saleReturnData changed:', {
      length: saleReturnData.length,
      items: saleReturnData,
      totalAmount: saleReturnData.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0)
    });
  }, [saleReturnData]);

  // Synchronize selectedRows with saleReturnData when invoice changes
  useEffect(() => {
    if (returnData.invoice_number && invoiceDetails.length > 0) {
      // Find which items from this invoice are already in saleReturnData
      const existingReturnItems = saleReturnData.filter(item => 
        item.invoice_number === returnData.invoice_number
      );
      
      // Map them back to row indices
      const selectedIndices = invoiceDetails.map((detail, index) => {
        const rowKey = `${detail.invoice_number}|${detail.code}|${detail.product_name}|${detail.gross_weight}|${detail.total_price}|${index}`;
        return existingReturnItems.some(returnItem => returnItem.original_item_id === rowKey) ? index : -1;
      }).filter(index => index !== -1);
      
      console.log('[EstimateSaleReturnForm] Syncing selectedRows with saleReturnData:', {
        invoiceNumber: returnData.invoice_number,
        existingReturnItems: existingReturnItems.length,
        selectedIndices,
        invoiceDetailsLength: invoiceDetails.length
      });
      
      setSelectedRows(selectedIndices);
      setIsAllSelected(selectedIndices.length === invoiceDetails.length && invoiceDetails.length > 0);
    }
  }, [returnData.invoice_number, invoiceDetails, saleReturnData]);

  // Fetch available invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await fetch(`${baseURL}/get-unique-repair-details`);
        if (!response.ok) {
          throw new Error("Failed to fetch invoices");
        }
        const data = await response.json();
        
        // Filter invoices for sales transactions only (like original implementation)
        const salesInvoices = data.filter(item => item.transaction_status === 'Sales');
        setFilteredInvoices(salesInvoices);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      }
    };

    fetchInvoices();
  }, []);

  // Helpers to mirror v1 invoice filtering/formatting
  const formatInvoiceDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  const formatDateForCompare = (dateString) => {
    const d = new Date(dateString);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getFilteredInvoices = () => {
    if (!filterDate) return filteredInvoices;
    const wanted = formatDateForCompare(filterDate);
    return filteredInvoices.filter((invoice) => formatDateForCompare(invoice.date) === wanted);
  };

  // Fetch invoice details when invoice number changes
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!returnData.invoice_number) {
        setInvoiceDetails([]);
        setSelectedRows([]);
        setIsAllSelected(false);
        return;
      }

      try {
        const response = await fetch(`${baseURL}/getsales/${returnData.invoice_number}`);
        if (!response.ok) {
          throw new Error("Failed to fetch invoice details");
        }
        const data = await response.json();
        
        // Filter out items that are already returned
        const availableItems = data.filter(item => item.status !== 'Sale Returned');
        setInvoiceDetails(availableItems);

        // Helper matcher to robustly identify previously selected rows
        const isSameRow = (item, r, currentInvoice) => {
          const invMatches = (r.invoice_number || r.original_invoice_number) === currentInvoice;
          if (!invMatches) return false;
          const rowKey = item.id || `${item.code}|${item.rate}|${item.total_price}|${item.gross_weight}`;
          if (r.original_item_id && r.original_item_id === rowKey) return true;
          // Fallback by attributes when original_item_id is not persisted
          const codeMatch = String(item.code || '') === String(r.code || '');
          const rateDiff = Math.abs((parseFloat(item.rate) || 0) - (parseFloat(r.rate) || 0));
          const totalDiff = Math.abs((parseFloat(item.total_price) || 0) - (parseFloat(r.total_price) || 0));
          const gwDiff = Math.abs((parseFloat(item.gross_weight) || 0) - (parseFloat(r.gross_weight) || 0));
          return codeMatch && (totalDiff < 0.01 || rateDiff < 0.01 || gwDiff < 0.001);
        };

        // Rehydrate checkbox selections from parent saleReturnData for the current invoice
        const selectedIndexes = availableItems
          .map((item, idx) => ({ item, idx }))
          .filter(({ item }) => saleReturnData.some(r => isSameRow(item, r, returnData.invoice_number)))
          .map(({ idx }) => idx);

        setSelectedRows(selectedIndexes);
        setIsAllSelected(selectedIndexes.length > 0 && selectedIndexes.length === availableItems.length);
      } catch (error) {
        console.error("Error fetching invoice details:", error);
        setInvoiceDetails([]);
        setSelectedRows([]);
        setIsAllSelected(false);
      }
    };

    fetchInvoiceDetails();
  }, [returnData.invoice_number, saleReturnData]);

  const handleInvoiceChange = (e) => {
    const selectedInvoiceNumber = e.target.value;
    if (!selectedInvoiceNumber) {
      // Clear selection and dependent state
      setReturnData(prev => ({ ...prev, invoice_number: '' }));
      setInvoiceDetails([]);
      setSelectedRows([]);
      setIsAllSelected(false);
      // Also clear any saleReturnData entries tied to previous invoice_number
      setSaleReturnData(prev => Array.isArray(prev) ? prev.filter(r => r.invoice_number !== prev.invoice_number) : []);
      return;
    }

    setReturnData(prev => ({
      ...prev,
      invoice_number: selectedInvoiceNumber,
    }));
  };

  const handleCheckboxChange = (event, index) => {
    const isChecked = event.target.checked;
    const item = invoiceDetails[index];
    
    // Create a unique row key using multiple identifiers to ensure uniqueness
    const rowKey = `${item.invoice_number}|${item.code}|${item.product_name}|${item.gross_weight}|${item.total_price}|${index}`;
    
    console.log('[EstimateSaleReturnForm] Checkbox change:', {
      index,
      isChecked,
      item,
      rowKey,
      selectedRows: selectedRows
    });
    
    if (isChecked) {
      // Add item to selected rows for UI
      setSelectedRows(prev => {
        const next = [...prev, index];
        console.log('[EstimateSaleReturnForm] Updated selectedRows:', { prev, next, length: next.length });
        setIsAllSelected(next.length === invoiceDetails.length);
        return next;
      });
      
      // Immediately add item to sale return data
      const returnItem = {
        id: Date.now() + index,
        invoice_number: returnData.invoice_number,
        code: item.code,
        product_name: item.product_name,
        metal_type: item.metal_type,
        purity: item.purity,
        gross_weight: item.gross_weight,
        stone_weight: item.stone_weight || 0,
        rate: item.rate,
        making_charges: item.making_charges || 0,
        hm_charges: item.hm_charges || 0,
        // Ensure numeric precision only, no string carry-over that can cause rounding drift
        total_price: parseFloat(item.total_price) || 0,
        return_date: returnData.date,
        return_reason: returnData.return_reason,
        original_invoice_number: returnData.invoice_number,
        original_item_id: rowKey,
      };
      
      setSaleReturnData(prev => {
        const exists = prev.some(r => r.invoice_number === returnData.invoice_number && r.original_item_id === rowKey);
        const newData = exists ? prev : [...prev, returnItem];
        console.log('[EstimateSaleReturnForm] Adding return item:', {
          returnItem,
          previousLength: prev.length,
          newLength: newData.length,
          totalAmount: returnItem.total_price,
          allItems: newData
        });
        return newData;
      });
    } else {
      // Remove item from selected rows for UI
      setSelectedRows(prev => {
        const next = prev.filter(rowIndex => rowIndex !== index);
        console.log('[EstimateSaleReturnForm] Removing from selectedRows:', { prev, next, length: next.length });
        setIsAllSelected(false);
        return next;
      });
      
      // Remove item from sale return data (match by invoice and original_item_id)
      setSaleReturnData(prev => {
        const newData = prev.filter(returnItem => 
          !(returnItem.invoice_number === returnData.invoice_number && returnItem.original_item_id === rowKey)
        );
        console.log('[EstimateSaleReturnForm] Removing return item:', {
          rowKey,
          previousLength: prev.length,
          newLength: newData.length,
          removedItems: prev.filter(returnItem => 
            returnItem.invoice_number === returnData.invoice_number && returnItem.original_item_id === rowKey
          ),
          remainingItems: newData
        });
        return newData;
      });
    }
  };

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    
    console.log('[EstimateSaleReturnForm] Select all change:', {
      isChecked,
      invoiceDetailsLength: invoiceDetails.length,
      currentSelectedRows: selectedRows
    });
    
    if (isChecked) {
      // Add all items to selected rows for UI
      setSelectedRows(invoiceDetails.map((_, index) => index));
      setIsAllSelected(true);
      
      // Immediately add all items to sale return data
      const returnItems = invoiceDetails.map((item, index) => ({
        id: Date.now() + index,
        invoice_number: returnData.invoice_number,
        code: item.code,
        product_name: item.product_name,
        metal_type: item.metal_type,
        purity: item.purity,
        gross_weight: item.gross_weight,
        stone_weight: item.stone_weight || 0,
        rate: item.rate,
        making_charges: item.making_charges || 0,
        hm_charges: item.hm_charges || 0,
        total_price: item.total_price,
        return_date: returnData.date,
        return_reason: returnData.return_reason,
        original_invoice_number: returnData.invoice_number,
        original_item_id: `${item.invoice_number}|${item.code}|${item.product_name}|${item.gross_weight}|${item.total_price}|${index}`,
      }));
      
      setSaleReturnData(prev => {
        // Merge uniquely by invoice + original_item_id
        const byKey = new Map(prev.map(r => [`${r.invoice_number}|${r.original_item_id}`, r]));
        for (const ri of returnItems) {
          const key = `${ri.invoice_number}|${ri.original_item_id}`;
          if (!byKey.has(key)) byKey.set(key, ri);
        }
        return Array.from(byKey.values());
      });
    } else {
      // Remove all items from selected rows for UI
      setSelectedRows([]);
      setIsAllSelected(false);
      
      // Remove all items from sale return data for this invoice
      setSaleReturnData(prev => {
        const newData = prev.filter(returnItem => 
          returnItem.invoice_number !== returnData.invoice_number
        );
        return newData;
      });
    }
  };

  const clearAllSelections = () => {
    console.log('[EstimateSaleReturnForm] Clearing all selections');
    setSelectedRows([]);
    setIsAllSelected(false);
    
    // Remove all items from sale return data for this invoice
    setSaleReturnData(prev => {
      const newData = prev.filter(returnItem => 
        returnItem.invoice_number !== returnData.invoice_number
      );
      return newData;
    });
  };

  return (
    <div className="estimate-sale-return-form">
      {/* <h5>Sale Return Form</h5> */}
      
      {/* Invoice Selection */}
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <InputField
            label="Invoice Number"
            name="invoice_number"
            type="select"
            value={returnData.invoice_number || ''} 
            onChange={handleInvoiceChange}
            options={getFilteredInvoices().map((invoice) => ({
              value: invoice.invoice_number,
              label: `${invoice.invoice_number} - ${formatInvoiceDate(invoice.date)}`,
            }))}
          />
        </Col>
        <Col xs={12} md={6}>
          <InputField
            label="Date:"
            name="date"
            value={filterDate}
            type="date"
            onChange={(e) => setFilterDate(e.target.value)}
            max={new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })}
          />
        </Col>
      </Row>

      {/* Invoice Products Table (mirroring v1) */}
      {invoiceDetails.length > 0 && (
        <div className="mb-3">
          {/* Debug info and controls */}
          {/* <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px', 
            marginBottom: '10px',
            fontSize: '12px'
          }}>
            <strong>Selection Status:</strong> {selectedRows.length} of {invoiceDetails.length} rows selected
            {selectedRows.length > 0 && (
              <span style={{ marginLeft: '10px' }}>
                (Selected indices: {selectedRows.join(', ')})
              </span>
            )}
            {selectedRows.length > 0 && (
              <button 
                onClick={clearAllSelections}
                style={{ 
                  marginLeft: '10px', 
                  padding: '2px 8px', 
                  fontSize: '10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                Clear All
              </button>
            )}
          </div> */}
          
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr style={{ fontSize: '13px' }}>
                  <th>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAllChange}
                    />
                  </th>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Code</th>
                  <th>Product Name</th>
                  <th>Gross Wt</th>
                  <th>Total Wt</th>
                  <th>Rate</th>
                  <th>Tax Amount</th>
                  <th>Total Price</th>
                </tr>
              </thead>
              <tbody>
                {invoiceDetails.map((detail, index) => (
                  <tr 
                    key={`${detail.invoice_number}-${index}`} 
                    style={{ 
                      fontSize: '12px',
                      backgroundColor: selectedRows.includes(index) ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(index)}
                        onChange={(event) => handleCheckboxChange(event, index)}
                      />
                    </td>
                    <td>{new Date(detail.date).toLocaleDateString('en-GB')}</td>
                    <td>{detail.invoice_number}</td>
                    <td>{detail.code}</td>
                    <td>{detail.product_name}</td>
                    <td>{detail.gross_weight}</td>
                    <td>{detail.total_weight_av}</td>
                    <td>{detail.rate}</td>
                    <td>{detail.tax_amt}</td>
                    <td>{detail.total_price}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* Summary section: reuse v1 component to match UI exactly */}
      {(() => {
        const selectedDetails = selectedRows.map((i) => invoiceDetails[i]).filter(Boolean);
        
        // Use the actual net values and exclude non-refundable HM charges from SR
        const salesNetAmount = selectedDetails.reduce((sum, detail) => {
          const net = parseFloat(detail?.total_price) || 0;
          const hm = parseFloat(detail?.hm_charges) || 0;
          return sum + (net - hm);
        }, 0);
        
        // Calculate taxable amount by subtracting tax from net amount for display purposes
        const totalTaxAmount = selectedDetails.reduce((sum, detail) => sum + (parseFloat(detail?.tax_amt) || 0), 0);
        const salesTaxableAmount = salesNetAmount - totalTaxAmount;
        const salesTaxAmount = totalTaxAmount;

        return (
          <SalesPaymentDetails
            invoiceDetails={invoiceDetails}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            isAllSelected={isAllSelected}
            setIsAllSelected={setIsAllSelected}
            resetSaleReturnForm={() => {}}
            salesTaxableAmount={salesTaxableAmount}
            salesTaxAmount={salesTaxAmount}
            salesNetAmount={salesNetAmount}
            handleCheckout={() => {}}
          />
        );
      })()}

      {filteredInvoices.length === 0 && selectedMobile && (
        <div className="text-center mt-3">
          <p>No sales invoices found for this customer</p>
        </div>
      )}
    </div>
  );
};

export default EstimateSaleReturnForm; 